// import LoanApplicationPage from './pages/LoanApplication';

// Add this to your routes:
// <Route path="/loan-application" element={<LoanApplicationPage />} />
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { Session, User } from '@supabase/supabase-js';

// Import your existing components
import ForeclosureQuestionnaire from './components/ForeclosureQuestionnaire';
import { PrivacyPolicyPage, TermsConditionsPage, CookiesPolicyPage, DisclaimerPage, ReturnRefundPage } from './pages/LegalPages';

// Header/Navigation Component
const Header = () => (
  <header className="bg-white shadow-sm border-b">
    {/* Top banner */}
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
        <div className="flex items-center space-x-2">
          <span className="text-yellow-300">💰</span>
          <span>Private Money Financing Available Through RepMotivatedSeller</span>
          <span className="hidden md:inline">• Residential & multifamily investment loans • 8-15% rates • $30K-FHA cap • 6-24 months</span>
        </div>
        <button className="bg-white text-blue-600 px-3 py-1 rounded text-xs font-medium hover:bg-gray-100">
          Learn More
        </button>
      </div>
    </div>

    {/* Main navigation */}
    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center space-x-3">
            <div className="bg-blue-600 text-white p-2 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">RepMotivatedSeller</span>
          </Link>
        </div>

        <div className="hidden md:flex items-center space-x-8">
          <Link to="/" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Home</Link>
          <Link to="/about" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">About</Link>
          <Link to="/services" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Services</Link>
          <Link to="/professionals" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">For Professionals</Link>
          <Link to="/resources" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Resources</Link>
          <Link to="/contact" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Contact</Link>
          <Link to="/login" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">Login</Link>
          <Link to="/foreclosure-help" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium">Get Help Now</Link>
        </div>
      </div>
    </nav>
  </header>
);

// Professional Homepage Component
const HomePage = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Hero Section */}
    <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Stop Foreclosure. Find Solutions.
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            Professional foreclosure assistance and real estate solutions for families and investors
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/foreclosure-help"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Help Now - Free Consultation
            </Link>
            <Link
              to="/professionals"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              For Real Estate Professionals
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Services Section */}
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Foreclosure Solutions</h2>
          <p className="text-xl text-gray-600">We help homeowners and work with real estate professionals to find the best outcomes</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="bg-blue-100 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">For Homeowners</h3>
            <ul className="text-gray-600 space-y-2 mb-6">
              <li>• Stop foreclosure proceedings</li>
              <li>• Loan modification assistance</li>
              <li>• Short sale negotiations</li>
              <li>• Cash offers for quick sales</li>
              <li>• Credit protection strategies</li>
            </ul>
            <Link
              to="/foreclosure-help"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-block"
            >
              Get Free Help
            </Link>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="bg-green-100 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">For Real Estate Agents</h3>
            <ul className="text-gray-600 space-y-2 mb-6">
              <li>• Pre-foreclosure lead referrals</li>
              <li>• Professional training programs</li>
              <li>• Market analysis tools</li>
              <li>• Commission opportunities</li>
              <li>• Continuing education credits</li>
            </ul>
            <Link
              to="/professionals"
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors inline-block"
            >
              Join Our Network
            </Link>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="bg-purple-100 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zM14 6a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h6z"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">For Investors</h3>
            <ul className="text-gray-600 space-y-2 mb-6">
              <li>• Pre-foreclosure opportunities</li>
              <li>• Private money lending</li>
              <li>• Wholesale deals</li>
              <li>• Market data access</li>
              <li>• Investment analysis tools</li>
            </ul>
            <Link
              to="/professionals"
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-block"
            >
              View Opportunities
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Stats Section */}
    <section className="bg-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
            <div className="text-gray-600">Families Helped</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-blue-600 mb-2">$50M+</div>
            <div className="text-gray-600">In Saved Equity</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-blue-600 mb-2">98%</div>
            <div className="text-gray-600">Success Rate</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-blue-600 mb-2">24hr</div>
            <div className="text-gray-600">Response Time</div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="bg-blue-600 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Find Your Solution?</h2>
        <p className="text-xl mb-8 text-blue-100">Get a free consultation with our foreclosure specialists today</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/foreclosure-help"
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Your Free Consultation
          </Link>
          <Link
            to="/contact"
            className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
          >
            Call (877) 806-4677
          </Link>
        </div>
      </div>
    </section>
  </div>
);

// Footer Component with Legal Links
const Footer = () => (
  <footer className="bg-gray-900 text-white py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-blue-600 text-white p-2 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
              </svg>
            </div>
            <span className="text-xl font-bold">RepMotivatedSeller</span>
          </div>
          <p className="text-gray-300 mb-4">
            Professional foreclosure assistance and real estate solutions for families and investors.
          </p>
          <p className="text-gray-400 text-sm">
            © 2025 Sofie's Investment Group LLC. All rights reserved.
          </p>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
          <ul className="space-y-2">
            <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
            <li><Link to="/services" className="text-gray-300 hover:text-white">Services</Link></li>
            <li><Link to="/professionals" className="text-gray-300 hover:text-white">For Professionals</Link></li>
            <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
          </ul>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-4">Legal</h3>
          <ul className="space-y-2">
            <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms-conditions" className="text-gray-300 hover:text-white">Terms & Conditions</Link></li>
            <li><Link to="/cookies-policy" className="text-gray-300 hover:text-white">Cookies Policy</Link></li>
            <li><Link to="/disclaimer" className="text-gray-300 hover:text-white">Disclaimer</Link></li>
            <li><Link to="/return-refund" className="text-gray-300 hover:text-white">Return & Refund</Link></li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-gray-800 pt-8 mt-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-400 text-sm">
            <p>Professional foreclosure assistance • Licensed real estate services • Confidential consultations</p>
          </div>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <a href="tel:8337289278" className="text-gray-300 hover:text-white">
              <span className="sr-only">Phone</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
              </svg>
            </a>
            <a href="mailto:melvin@repmotivatedseller.shoprealestatespace.org" className="text-gray-300 hover:text-white">
              <span className="sr-only">Email</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

// Foreclosure Help Page (wraps your existing component)
const ForeclosureHelpPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <ForeclosureQuestionnaire />
  </div>
);

// Simple placeholder pages
const AboutPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <div className="max-w-4xl mx-auto px-4">
      <h1 className="text-4xl font-bold mb-8">About RepMotivatedSeller</h1>
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <p className="text-lg text-gray-600 mb-6">
          RepMotivatedSeller is dedicated to helping families facing foreclosure find solutions that protect their financial future. 
          We work with homeowners, real estate professionals, and investors to create win-win outcomes in challenging situations.
        </p>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Do</h2>
        <ul className="space-y-2 text-gray-600">
          <li>• Provide free foreclosure assistance consultations</li>
          <li>• Negotiate with lenders for loan modifications</li>
          <li>• Facilitate short sales and deed-in-lieu arrangements</li>
          <li>• Connect families with qualified real estate professionals</li>
          <li>• Offer cash purchase options for quick resolutions</li>
        </ul>
      </div>
    </div>
  </div>
);

const ServicesPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-4xl font-bold mb-12 text-center">Our Services</h1>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Foreclosure Prevention</h3>
          <p className="text-gray-600">
            We work directly with your lender to stop foreclosure proceedings and find alternative solutions.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Loan Modification</h3>
          <p className="text-gray-600">
            Professional assistance with loan modification applications to reduce payments and interest rates.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Short Sale Assistance</h3>
          <p className="text-gray-600">
            Expert guidance through the short sale process to minimize credit impact and avoid deficiency judgments.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ProfessionalsPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-4xl font-bold mb-8 text-center">For Real Estate Professionals</h1>
      <div className="grid md:grid-cols-2 gap-12">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">For Real Estate Agents</h2>
          <ul className="space-y-3 text-gray-600 mb-6">
            <li>• Receive qualified pre-foreclosure leads</li>
            <li>• Earn referral commissions on closed deals</li>
            <li>• Access professional training programs</li>
            <li>• Get continuing education credits</li>
            <li>• Use our market analysis tools</li>
          </ul>
          <Link to="/contact" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 inline-block">
            Join Our Agent Network
          </Link>
        </div>
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">For Investors</h2>
          <ul className="space-y-3 text-gray-600 mb-6">
            <li>• Access pre-foreclosure opportunities</li>
            <li>• Private money lending programs</li>
            <li>• Wholesale deal pipeline</li>
            <li>• Market data and analytics</li>
            <li>• Investment analysis tools</li>
          </ul>
          <Link to="/contact" className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 inline-block">
            View Investment Opportunities
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const ResourcesPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <div className="max-w-4xl mx-auto px-4">
      <h1 className="text-4xl font-bold mb-8">Resources</h1>
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <p className="text-lg text-gray-600">Educational content and tools for homeowners and professionals.</p>
      </div>
    </div>
  </div>
);

const ContactPage = () => (
  <div className="min-h-screen bg-gray-50 py-12">
    <div className="max-w-4xl mx-auto px-4">
      <h1 className="text-4xl font-bold mb-12 text-center">Contact Us</h1>
      <div className="grid md:grid-cols-2 gap-12">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900">Phone</h3>
              <p className="text-gray-600">(877) 806-4677</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Email</h3>
              <p className="text-gray-600">help@repmotivatedseller.org</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Hours</h3>
              <p className="text-gray-600">Monday - Friday: 8AM - 6PM PST</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Response</h2>
          <p className="text-gray-600 mb-6">
            For fastest service, fill out our foreclosure help form and we'll contact you within 24 hours.
          </p>
          <Link to="/foreclosure-help" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 inline-block">
            Get Help Now
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Your existing components
const Login = () => <div className="flex items-center justify-center h-screen bg-gray-100"><h1 className="text-2xl font-bold">Login Page</h1></div>;
const AdminDashboard = () => <div className="p-8"><h1 className="text-3xl font-bold">Admin Dashboard</h1></div>;
const ResponseDetails = () => <div className="p-8"><h1 className="text-3xl font-bold">Response Details</h1></div>;
const NotFound = () => <div className="flex items-center justify-center h-screen bg-gray-100"><h1 className="text-4xl font-bold text-gray-700">404 - Page Not Found</h1></div>;

/**
 * The root component of the application.
 * It manages routing and global authentication state using Supabase.
 */
function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up a subscription to listen for authentication state changes.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Fetch the initial session state to check if the user is already logged in.
    const fetchSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    // Cleanup the subscription when the component unmounts.
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Display a loading indicator while the session is being fetched.
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 font-sans">
        <Header />
        
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/professionals" element={<ProfessionalsPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/foreclosure-help" element={<ForeclosureHelpPage />} />
          <Route path="/login" element={<Login />} />
          
          {/* Protected routes for authenticated admin users */}
          <Route
            path="/admin/dashboard"
            element={user ? <AdminDashboard /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/admin/dashboard/response/:id"
            element={user ? <ResponseDetails /> : <Navigate to="/login" replace />}
          />
          
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-conditions" element={<TermsConditionsPage />} />
          <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/return-refund" element={<ReturnRefundPage />} />
          
          {/* Legacy routes for compatibility */}
          <Route path="/questionnaire" element={<ForeclosureHelpPage />} />
          <Route path="/form" element={<ForeclosureHelpPage />} />
          
          {/* Catch-all route for 404 Not Found pages */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;